import { useState, useEffect, useCallback } from 'react';
import { getMonthAgreement36Data, getMonthInfoAgreement36Data, getDetailStaffAgreement36 } from 'api/agreement36';
import useToastNotification from 'hooks/useToastNotification';
import moment from 'moment';
import { useOrgTreesByOrgCSVCode } from 'hooks/useOrgTreesOptions';
import { Attend36AgreementsAlertData } from './../type';

const { errorNotification, successNotification } = useToastNotification();

export type MonthlyAgreement36Data = {
  agreementsId: string;
  targetYear: number;
  applyStartMonth: Date;
  monthlyOvertimeList: Array<number>|null;
  monthlyOvertimeHourList: Array<number>|null;
  monthlyOverAndHolidayTime: number;
  monthlyOverAndHolidayTimeHour: number;
}
export type MonthlyInfoAgreement36Data = {
  staffCode: string;
  staffName: string;
  orgCode: string;
  orgName: string;
  remainDays: number;
  overTimeHour: number;
  holidayTimeHour: number;
  overHolidayTimeHour: number;
  overTimeFlag: number;
  overHolidayTimeFlag: number;
}

const initMonthlyAgreement36Data: MonthlyAgreement36Data = {
  agreementsId: '',
  targetYear: 0,
  applyStartMonth: new Date(),
  monthlyOvertimeList: null,
  monthlyOvertimeHourList: null,
  monthlyOverAndHolidayTime: 0,
  monthlyOverAndHolidayTimeHour: 0,
}

const testMonthlyAgreement36Data: MonthlyAgreement36Data = {
  agreementsId: '84518214-de1d-4750-9314-38136d1e3157',
  targetYear: 2020,
  applyStartMonth: new Date(),
  monthlyOvertimeList: [1800,2100,2400,3600],
  monthlyOvertimeHourList: [30,35,40,60],
  monthlyOverAndHolidayTime: 60000,
  monthlyOverAndHolidayTimeHour: 100,
}

export const useMonthlyAgreement36Form = () => {
  const staffCode = sessionStorage.getItem('loginUser.staffCode') || "";
  const [orgCode, setOrgCode] = useState(sessionStorage.getItem('loginUser.orgCode') || '');
  const [checkboxThreshold, setCheckboxThreshold] = useState<boolean[]>([true, true, true, true])
  const [checkboxHoliday, setCheckboxHoliday] = useState<boolean>(true);
  const [monthlyAgreement36Data, setMonthlyAgreement36Data] = useState<MonthlyAgreement36Data>(initMonthlyAgreement36Data);
  const [monthlyInfoData, setMonthlyInfoData] = useState<MonthlyInfoAgreement36Data[]>();
  const [staffDetail, setStaffDetail] = useState<Attend36AgreementsAlertData[]>();
  const [flag, setFlag] = useState(false)
  const [isOpenModal, setIsOpenModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [applyDate, setApplyDate] = useState(new Date());
  const addAllItem = sessionStorage.getItem('loginUser.site') === '3';
  const orgTreesOptions = useOrgTreesByOrgCSVCode(applyDate, applyDate, setIsLoading, addAllItem);

  const fetchMonthInfo = async (monthlyData: MonthlyAgreement36Data, applyDate: Date, checkboxMonth: boolean[], checkboxHoliday: boolean) => {
    setIsLoading(true);
    let params = {
      targetMonth: moment(applyDate).format('YYYY/MM/01'),
      orgCode,
      overTimeAry: monthlyData.monthlyOvertimeList?.filter((item, index) => checkboxMonth[index]).join(),
      overHolidayTime: checkboxHoliday ? monthlyData.monthlyOverAndHolidayTime : null
    }
    try {
      getMonthInfoAgreement36Data(params).then((response: any) => {
        setIsLoading(false);
        setMonthlyInfoData(response);
      }).catch((error: any) => {
        setIsLoading(false);
        if (error.status === 400) {
          if (error.data.errors && error.data.errors.length > 0) {
            errorNotification(
              error.data.errors.map((x: any) => x.defaultMessage)
            );
          } else if (error.data.defaultMessage) {
            errorNotification(error.data.defaultMessage);
          } else {
            errorNotification(error.data.message);
          }
        } else {
          errorNotification("サーバー側でエラーが発生しました。");
        }  
      })
    } catch (error) {
      errorNotification('サーバー側でエラーが発生しました。');
      setIsLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setFlag(false);
    try {
      getMonthAgreement36Data(moment(applyDate).format('YYYY/MM/01')).then((response: any) => {
        if(!response){
          errorNotification('36協定アラートマスタが登録されていません');
        }
        setMonthlyAgreement36Data(response);
        fetchMonthInfo(
          response, applyDate, [true, true, true, true], true
        ).then(() => {
          setCheckboxThreshold([true, true, true, true]);
          setCheckboxHoliday(true);
        });
      }).catch((error: any) => {
        setIsLoading(false);
        if (error.status === 400) {
          if (error.data.errors && error.data.errors.length > 0) {
            errorNotification(
              error.data.errors.map((x: any) => x.defaultMessage)
            );
          } else if (error.data.defaultMessage) {
            errorNotification(error.data.defaultMessage);
          } else {
            errorNotification(error.data.message);
          }
        } else {
          errorNotification("サーバー側でエラーが発生しました。");
        }  
      }).finally(() => {
        setFlag(true);
      })
    } catch (error) {
      errorNotification('サーバー側でエラーが発生しました。');
      setIsLoading(false);
    } finally{
      setIsLoading(false);
    }
  }, [applyDate]);

  const fetchDataDetailStaff = useCallback(async (staffCode: string) => {
    setIsLoading(true);
    let params = {
      staffCode,
      targetMonth: moment(applyDate).format('YYYY/MM/01')
    }
    try {
      getDetailStaffAgreement36(params).then((response: any) => {
        setStaffDetail(response);        
      }).catch((error: any) => {
        if (error.status === 400) {
          if (error.data.errors && error.data.errors.length > 0) {
            errorNotification(
              error.data.errors.map((x: any) => x.defaultMessage)
            );
          } else if (error.data.defaultMessage) {
            errorNotification(error.data.defaultMessage);
          } else {
            errorNotification(error.data.message);
          }
        } else {
          errorNotification("サーバー側でエラーが発生しました。");
        }  
      }).finally(() => {
        setIsLoading(false);
      })
    } catch (error) {
      errorNotification('サーバー側でエラーが発生しました。');
    } finally{
    }
  }, [applyDate]);

  useEffect(() => {
    if(flag){
      if(!monthlyAgreement36Data || !monthlyAgreement36Data.monthlyOvertimeHourList){
        errorNotification('36協定アラートマスタが登録されていません');
        return
      }
      fetchMonthInfo(monthlyAgreement36Data, applyDate, checkboxThreshold, checkboxHoliday);
    }
  }, [checkboxThreshold, checkboxHoliday, orgCode])

  useEffect(() => {
    fetchData();
  }, [applyDate]);

  const handleOpenDetail = (staffCode: string) => {
    setIsOpenModal(true);
    fetchDataDetailStaff(staffCode);
  }
 
  return {
    isLoading,
    applyDate,
    setApplyDate,
    staffCode,
    orgCode,
    setOrgCode,
    orgTreesOptions,
    checkboxThreshold,
    setCheckboxThreshold,
    monthlyAgreement36Data,
    checkboxHoliday,
    setCheckboxHoliday,
    monthlyInfoData,
    isOpenModal,
    setIsOpenModal,
    handleOpenDetail,
    staffDetail,
  };
};

export default useMonthlyAgreement36Form;
